import React, { forwardRef } from 'react';

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

const ThermalDocumentPreview = forwardRef(({ document }, ref) => {
  if (!document) return null;

  const paperWidth = document.paper_width === '58mm' ? '260px' : '360px';
  const isReceipt = document.document_type === 'receipt';

  return (
    <div
      ref={ref}
      className="mx-auto w-full rounded-sm border border-black bg-white px-3 py-4 text-black shadow-sm"
      style={{ maxWidth: paperWidth }}
      data-testid="thermal-document-preview"
    >
      <div className="border-b border-dashed border-black pb-2.5 text-center">
        <div className="text-base font-bold uppercase tracking-[0.08em]">{document.restaurant_name}</div>
        {isReceipt && document.restaurant_address && (
          <div className="mt-1 text-[10px]">{document.restaurant_address}</div>
        )}
        {isReceipt && document.restaurant_phone && (
          <div className="text-[10px]">{document.restaurant_phone}</div>
        )}
        <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em]">{isReceipt ? 'Customer Receipt' : 'Kitchen Ticket'}</div>
      </div>

      <div className="space-y-1 border-b border-dashed border-black py-2.5 text-[11px]">
        {document.metadata?.map((entry) => (
          <div key={`${entry.label}-${entry.value}`} className="flex justify-between gap-3">
            <span className="font-semibold uppercase">{entry.label}</span>
            <span className="text-right">{entry.label.toLowerCase().includes('time') ? formatTimestamp(entry.value) : entry.value}</span>
          </div>
        ))}
      </div>

      <div className="border-b border-dashed border-black py-2.5">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em]">Items</div>
        <div className="space-y-2.5">
          {document.items?.map((item, index) => (
            <div key={`${item.name}-${index}`} className="text-[13px]">
              <div className="flex gap-3">
                <span className="min-w-8 font-bold">x{item.quantity}</span>
                <span className="flex-1 font-semibold uppercase">{item.name}</span>
                {isReceipt && (
                  <span className="text-right text-[11px] font-medium">
                    {formatMoney(item.unit_price)} | {formatMoney(item.line_total)}
                  </span>
                )}
              </div>

              {!isReceipt && item.variants?.length > 0 && (
                <div className="mt-1 ps-8 text-[11px]">Variants: {item.variants.join(', ')}</div>
              )}

              {!isReceipt && item.modifiers?.length > 0 && (
                <div className="mt-1 ps-8 text-[11px]">Modifiers: {item.modifiers.join(', ')}</div>
              )}

              {!isReceipt && item.special_instructions && (
                <div className="mt-1 ps-8 text-[11px] font-medium">Special: {item.special_instructions}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isReceipt ? (
        <>
          <div className="border-b border-dashed border-black py-3 text-xs">
            <div className="space-y-1">
              {document.summary_lines?.map((line) => (
                <div key={`${line.label}-${line.value}`} className="flex items-center justify-between gap-3">
                  <span className={line.emphasis ? 'font-bold uppercase tracking-wide' : 'font-semibold uppercase'}>{line.label}</span>
                  <span className={line.emphasis ? 'font-bold' : ''}>{formatMoney(line.value)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-b border-dashed border-black py-3 text-xs">
            <div className="space-y-1">
              {document.payment_lines?.map((line) => (
                <div key={`${line.label}-${line.value}`} className="flex items-center justify-between gap-3">
                  <span className="font-semibold uppercase">{line.label}</span>
                  <span>{typeof line.value === 'number' ? formatMoney(line.value) : line.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-b border-dashed border-black py-3 text-center text-xs font-semibold uppercase tracking-[0.2em]">
            {document.footer_message || 'Thank you!'}
          </div>
        </>
      ) : (
        <div className="border-b border-dashed border-black py-2.5 text-[11px]">
          <div className="mb-1 font-semibold uppercase tracking-[0.2em]">General Notes</div>
          <div>{document.general_notes || 'None'}</div>
        </div>
      )}

      <div className="pt-2.5 text-center text-[10px] uppercase tracking-[0.2em] text-black/80">
        Generated {formatTimestamp(document.generated_at)}
      </div>
    </div>
  );
});

ThermalDocumentPreview.displayName = 'ThermalDocumentPreview';

export default ThermalDocumentPreview;